import {
  fetchApiGetV2,
  fetchApiPostV2,
  fetchApiPutV2,
  fetchApiDeleteV2,
} from "../../../../api/api";
import store from "../../../store";
import { UPDATE_WEBPAGES, DELETE_WEBPAGE } from "./pagesReducer";
import type {
  Page,
  PageLanguageRef,
  Pages,
  TranslationWebpage,
} from "./pagesTypes";

export type {
  Page,
  PageLanguageRef,
  Pages,
  TranslationWebpage,
} from "./pagesTypes";

function asTranslationList(raw: unknown): TranslationWebpage[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const o = item as Record<string, unknown>;
      const rawCode = o.languageCode;
      const languageCode =
        typeof rawCode === "string" && rawCode.trim() !== ""
          ? rawCode.trim()
          : "";
      const targetUuid = o.targetWebpageUuid ?? o.target_webpage_uuid;
      if (!languageCode) {
        return null;
      }
      const tw: TranslationWebpage = {
        languageCode,
        target_webpage_uuid:
          typeof targetUuid === "string" ? targetUuid : String(targetUuid ?? ""),
      };
      const tid = o.targetWebpageId ?? o.target_webpage_id;
      if (typeof tid === "number") {
        tw.target_webpage_id = tid;
      }
      return tw;
    })
    .filter((x): x is TranslationWebpage => x != null);
}

function str(v: unknown): string {
  return typeof v === "string" ? v : String(v ?? "");
}

function optStr(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v);
  return s === "" || s === "null" ? null : s;
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function languageCodeFromRawLanguageObject(lang: unknown): string {
  if (lang == null || typeof lang !== "object") return "";
  const o = lang as Record<string, unknown>;
  const direct = str(o.languageCode ?? "").trim();
  if (direct) return direct;
  const loc = str(o.locale ?? "").trim();
  if (loc) return loc;
  const short = str(o.shortname ?? "").trim();
  if (short) return short;
  const nested = o.language;
  if (nested != null && typeof nested === "object") {
    const n = nested as Record<string, unknown>;
    return str(n.shortname ?? n.locale ?? "").trim();
  }
  return "";
}

/** Top-level `languageCode`, nested `language`, or first `languageWebpages[]` child. */
function resolveWebpageLanguageCode(
  raw: Record<string, unknown>,
  mappedLanguageWebpages: Page[]
): string {
  const top = str(raw.languageCode ?? "").trim();
  if (top) return top;
  const fromLang = languageCodeFromRawLanguageObject(raw.language);
  if (fromLang) return fromLang;
  for (const child of mappedLanguageWebpages) {
    const c = str(child.languageCode ?? "").trim();
    if (c) return c;
  }
  return "";
}

/**
 * Resolve a page from `pages.list` using a template/API `webpages[]` entry
 * (V2 `uuid` string or legacy numeric `id`).
 */
export function findPageByWebpageRef(
  pages: Page[] | undefined | null,
  ref: number | string
): Page | undefined {
  if (pages == null || pages.length === 0) {
    return undefined;
  }
  if (typeof ref === "number" && Number.isFinite(ref)) {
    return pages.find((p) => p.id === ref);
  }
  const s = String(ref).trim();
  if (!s) {
    return undefined;
  }
  const byUuid = pages.find((p) => (p.uuid ?? "").trim() === s);
  if (byUuid) {
    return byUuid;
  }
  const n = Number(s);
  if (Number.isFinite(n) && String(n) === s) {
    return pages.find((p) => p.id === n);
  }
  return pages.find((p) => String(p.id) === s);
}

/**
 * Map one webpage from API JSON (camelCase V2) into normalized `Page`.
 * Accepts legacy snake_case keys when present.
 */
export function mapWebpageFromApi(raw: Record<string, unknown>): Page {
  const uuid = str(raw.uuid);
  const legacyId = Number(
    raw.legacyWebpageId ?? raw.legacy_webpage_id ?? raw.id ?? 0
  );

  const translationRaw =
    raw.translationWebpages ?? raw.translation_webpages ?? null;
  const languageWebpagesRaw =
    raw.languageWebpages ?? raw.language_webpages ?? null;

  const parentUuid =
    raw.parentWebpageUuid ?? raw.parent_webpage_uuid ?? null;
  const parentUuidStr =
    typeof parentUuid === "string" && parentUuid !== "" ? parentUuid : null;

  const languageWebpages = Array.isArray(languageWebpagesRaw)
    ? languageWebpagesRaw.map((row) =>
        mapWebpageFromApi(row as Record<string, unknown>)
      )
    : [];

  const languageCode = resolveWebpageLanguageCode(raw, languageWebpages);

  const page: Page = {
    uuid,
    id: Number.isFinite(legacyId) ? legacyId : 0,
    name: str(raw.name),
    pageType: str(raw.pageType ?? raw.page_type ?? "contentPage"),
    language:
      raw.language != null && typeof raw.language === "object"
        ? (raw.language as PageLanguageRef)
        : null,
    homepage: Boolean(raw.homepage),
    languageWebpages,
    page: str(raw.page),
    seoDescription: optStr(raw.seoDescription ?? raw.seo_description),
    seoKeyword: optStr(raw.seoKeyword ?? raw.seo_keyword),
    seoNoIndex:
      raw.seoNoIndex === true ||
      raw.seo_no_index === true ||
      raw.seoNoIndex === 1 ||
      raw.seo_no_index === 1,
    seoTitle: optStr(raw.seoTitle ?? raw.seo_title),
    parentWebpageId: numOrNull(
      raw.parentWebpageId ?? raw.parent_webpage_id
    ),
    parentWebpageUuid: parentUuidStr,
    seoOgTitle: optStr(raw.seoOgTitle ?? raw.seo_og_title),
    seoOgDescription: optStr(raw.seoOgDescription ?? raw.seo_og_description),
    seoOgUrl: optStr(raw.seoOgUrl ?? raw.seo_og_url),
    seoOgImageUrl: optStr(raw.seoOgImageUrl ?? raw.seo_og_image_url),
    seoOgType: optStr(raw.seoOgType ?? raw.seo_og_type),
    seoTwitterCard: optStr(raw.seoTwitterCard ?? raw.seo_twitter_card),
    translationWebpages:
      translationRaw != null ? asTranslationList(translationRaw) : null,
    status: str(raw.status),
    createdAt: str(raw.createdAt ?? raw.created_at),
    url: str(raw.url),
    updatedAt: str(raw.updatedAt ?? raw.updated_at),
    websiteUuid:
      typeof raw.websiteUuid === "string"
        ? raw.websiteUuid
        : typeof raw.website_uuid === "string"
          ? raw.website_uuid
          : undefined,
    websiteId:
      typeof raw.websiteId === "number"
        ? raw.websiteId
        : typeof raw.website_id === "number"
          ? raw.website_id
          : undefined,
    languageCode,
    languageGroup: numOrNull(raw.languageGroup ?? raw.language_group),
    deletedAt: optStr(raw.deletedAt ?? raw.deleted_at),
    seoTwitterTitle: optStr(raw.seoTwitterTitle ?? raw.seo_twitter_title),
    seoTwitterDescription: optStr(
      raw.seoTwitterDescription ?? raw.seo_twitter_description
    ),
    seoTwitterImage: optStr(raw.seoTwitterImage ?? raw.seo_twitter_image),
  };

  return page;
}

/** Resolve parent + translation legacy ids for tree UI and v1 bridge. */
export function enrichWebpagesList(list: Page[]): Page[] {
  const uuidToId = new Map<string, number>();
  for (const p of list) {
    if (p.uuid) {
      uuidToId.set(p.uuid, p.id);
    }
  }

  return list.map((page) => {
    let parentWebpageId = page.parentWebpageId;
    if (page.parentWebpageUuid) {
      parentWebpageId =
        uuidToId.get(page.parentWebpageUuid) ?? parentWebpageId;
    }

    const translationWebpages = page.translationWebpages
      ? page.translationWebpages.map((t) => {
          const target_webpage_id =
            t.target_webpage_id ??
            (t.target_webpage_uuid
              ? uuidToId.get(t.target_webpage_uuid)
              : undefined);
          return {
            ...t,
            ...(target_webpage_id != null ? { target_webpage_id } : {}),
          };
        })
      : null;

    return {
      ...page,
      parentWebpageId,
      translationWebpages,
    };
  });
}

function pageToUpdatePayload(page: Page): Record<string, unknown> {
  const translationWebpages = (page.translationWebpages ?? [])
    .filter((t) => t.target_webpage_uuid)
    .map((t) => ({
      targetWebpageUuid: t.target_webpage_uuid,
      languageCode: t.languageCode,
    }))
    .filter((t) => t.languageCode !== "");

  const payload: Record<string, unknown> = {
    name: page.name,
    pageType: page.pageType,
    url: page.url,
    languageCode: page.languageCode,
    seoNoIndex: page.seoNoIndex,
    seoTitle: page.seoTitle,
    seoKeyword: page.seoKeyword,
    seoDescription: page.seoDescription,
    seoOgTitle: page.seoOgTitle,
    seoOgDescription: page.seoOgDescription,
    seoOgUrl: page.seoOgUrl,
    seoOgImageUrl: page.seoOgImageUrl,
    seoOgType: page.seoOgType,
    seoTwitterCard: page.seoTwitterCard,
    translationWebpages,
  };

  if (page.parentWebpageUuid) {
    payload.parentWebpageUuid = page.parentWebpageUuid;
  } else if (
    page.parentWebpageId != null &&
    page.parentWebpageId !== 0
  ) {
    payload.parentWebpageId = page.parentWebpageId;
  } else {
    payload.parentWebpageId = null;
  }

  return payload;
}

const PagesAPI = {
  loadWebpages,
  saveWebpageByUuid,
  addWebpage,
  deleteWebpage,
  duplicateWebpage,
};
export default PagesAPI;

export function loadWebpages() {
  return fetchApiGetV2<unknown[]>(`/webpages`, {}).then((res) => {
    const rows = Array.isArray(res) ? res : [];
    const mapped = rows.map((r) =>
      mapWebpageFromApi(r as Record<string, unknown>)
    );
    store.dispatch(UPDATE_WEBPAGES(enrichWebpagesList(mapped)));
  });
}

function deleteWebpage(webpageUuid: string) {
  return fetchApiDeleteV2(`/webpages/${webpageUuid}`, {}).then(() => {
    store.dispatch(DELETE_WEBPAGE({ uuid: webpageUuid }));
  });
}

function saveWebpageByUuid(webpageUuid: string, data: Page) {
  return fetchApiPutV2<Record<string, unknown>>(
    `/webpages/${webpageUuid}`,
    pageToUpdatePayload(data)
  )
    .then((server) => {
      const serverPage =
        server && typeof server === "object"
          ? mapWebpageFromApi(server as Record<string, unknown>)
          : { ...data };
      const list = store.getState().builder.pages.list as Page[];
      const idx = list.findIndex((p) => p.uuid === webpageUuid);
      const nextList = [...list];
      if (idx >= 0) {
        nextList[idx] = serverPage;
      } else {
        nextList.push(serverPage);
      }
      store.dispatch(UPDATE_WEBPAGES(enrichWebpagesList(nextList)));
    })
    .catch((e) => console.log(e));
}

function duplicateWebpage(webpageUuid: string) {
  return fetchApiPostV2<Record<string, unknown>>(
    `/webpages/${webpageUuid}/duplicate`
  ).then((res) => {
    const newPage = mapWebpageFromApi(res as Record<string, unknown>);
    const list = store.getState().builder.pages.list as Page[];
    const merged = [...list, newPage];
    const enriched = enrichWebpagesList(merged);
    store.dispatch(UPDATE_WEBPAGES(enriched));
    return enriched.find((p) => p.uuid === newPage.uuid) ?? newPage;
  });
}

function addWebpage(data: {
  name: string;
  url: string;
  pageType?: string;
  languageCode?: string;
  parentWebpageUuid?: string | null;
  /** Resolved to UUID when parent row is identified by legacy id */
  parentWebpageId?: number | null;
}) {
  const body: Record<string, unknown> = {
    name: data.name,
    url: data.url,
    pageType: data.pageType ?? "contentPage",
  };
  if (data.languageCode != null && data.languageCode !== "") {
    body.languageCode = data.languageCode;
  }
  let parentUuid = data.parentWebpageUuid;
  if (
    !parentUuid &&
    data.parentWebpageId != null &&
    data.parentWebpageId !== 0
  ) {
    const list = store.getState().builder.pages.list as Page[];
    parentUuid =
      list.find((p) => p.id === data.parentWebpageId)?.uuid ?? null;
  }
  if (parentUuid) {
    body.parentWebpageUuid = parentUuid;
  }
  return fetchApiPostV2<Record<string, unknown>>(`/webpages`, body).then(
    (res) => {
      const newPage = mapWebpageFromApi(res as Record<string, unknown>);
      const list = store.getState().builder.pages.list as Page[];
      const idx = list.findIndex((p) => p.id === 0 || p.uuid === "");
      const nextList = [...list];
      if (idx >= 0) {
        nextList[idx] = newPage;
      } else {
        nextList.push(newPage);
      }
      const enriched = enrichWebpagesList(nextList);
      store.dispatch(UPDATE_WEBPAGES(enriched));
      return enriched.find((p) => p.uuid === newPage.uuid) ?? newPage;
    }
  );
}

/** @deprecated Use saveWebpageByUuid — kept for incremental refactors */
export const saveWebpageById = saveWebpageByUuid;
